import isPlainObject from 'lodash/isPlainObject'
import type { DataProvider, Identifier } from 'ra-core'

import { BULK_EDIT_DEFAULT_MAX_RECORDS, BULK_EDIT_DEFAULT_PER_PAGE } from './constants'
import type {
  BulkEditAutoAcceptMode,
  BulkEditListQuery,
  BulkEditListToolbarDefaults,
  BulkEditResourceDefinition,
  DefineBulkEditResourceInput,
} from './types'

const BULK_EDIT_AUTO_ACCEPT_MODE_SET = new Set<string>(['immediate', 'confirm-always', 'confirm-if-multiple'])

export function isBulkEditAutoAcceptMode(value: unknown): value is BulkEditAutoAcceptMode {
  return typeof value === 'string' && BULK_EDIT_AUTO_ACCEPT_MODE_SET.has(value)
}

export function isPlainObjectRecord(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value)
}

export function mergeBulkEditToolbarDefaults(
  partial?: Partial<BulkEditListToolbarDefaults>
): BulkEditListToolbarDefaults {
  return {
    sort: partial?.sort ?? { field: 'id', order: 'ASC' },
    maxRecords: partial?.maxRecords ?? BULK_EDIT_DEFAULT_MAX_RECORDS,
    ...(partial?.perPage !== undefined ? { perPage: partial.perPage } : {}),
  }
}

export function filterPatchToVisibleKeys(
  patch: Record<string, unknown>,
  visibleKeys: string[] | undefined
): Record<string, unknown> {
  if (visibleKeys === undefined) return patch
  if (visibleKeys.length === 0) return {}
  const allow = new Set(visibleKeys)
  return Object.fromEntries(Object.entries(patch).filter(([k]) => allow.has(k)))
}

export function buildBulkEditVisiblePatch(
  definition: BulkEditResourceDefinition,
  formValues: Record<string, unknown>,
  patchVisibilityKeys: string[] | undefined
): Record<string, unknown> {
  return filterPatchToVisibleKeys(definition.buildPatch(formValues), patchVisibilityKeys)
}

export function defineBulkEditResource(input: DefineBulkEditResourceInput): BulkEditResourceDefinition {
  return { ...input } as BulkEditResourceDefinition
}

export function mergeBulkEditInitialValues(
  definition: BulkEditResourceDefinition,
  initialValues?: Record<string, unknown>
): Record<string, unknown> {
  return { ...definition.getDefaultFormValues?.(), ...initialValues }
}

export async function fetchIdsForBulkEdit(
  dataProvider: DataProvider,
  resource: string,
  listQuery: BulkEditListQuery
): Promise<Identifier[]> {
  const perPage = listQuery.perPage ?? BULK_EDIT_DEFAULT_PER_PAGE
  const maxRecords = Math.min(listQuery.maxRecords ?? BULK_EDIT_DEFAULT_MAX_RECORDS, BULK_EDIT_DEFAULT_MAX_RECORDS)
  const sort = listQuery.sort ?? { field: 'id', order: 'ASC' as const }

  const ids: Identifier[] = []
  let page = 1

  while (ids.length < maxRecords) {
    const { data, total } = await dataProvider.getList(resource, {
      pagination: { page, perPage },
      sort,
      filter: listQuery.filter,
    })

    for (const row of data) {
      if (row?.id != null) {
        ids.push(row.id as Identifier)
        if (ids.length >= maxRecords) break
      }
    }

    if (data.length < perPage) break
    if (total != null && page * perPage >= total) break
    page += 1
  }

  return ids.slice(0, maxRecords)
}
